import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { sellerService } from "@/features/sellers/services/seller.service";
import { commissionService } from "../services/commission.service";
import type {
  CommissionPeriodParams,
  CommissionSummaryDTO,
} from "../types/commission.types";

interface UseCommissionSummaryOptions {
  initialPeriod: CommissionPeriodParams;
}

const SELLERS_PAGE_SIZE = 200;

const EMPTY_SUMMARY = (
  sellerId: number,
  sellerName: string,
): CommissionSummaryDTO => ({
  sellerId,
  sellerName,
  totalCommission: 0,
  rentalCount: 0,
  commissionsByType: [],
});

export function useCommissionSummary({
  initialPeriod,
}: UseCommissionSummaryOptions) {
  const [data, setData] = useState<CommissionSummaryDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<CommissionPeriodParams>(initialPeriod);
  const requestIdRef = useRef(0);
  // Controller da última request em voo. Ao disparar uma nova, aborta a
  // anterior — cancela na rede, não só ignora a resposta (requestIdRef).
  const controllerRef = useRef<AbortController | null>(null);

  const load = useCallback(async (target: CommissionPeriodParams) => {
    const requestId = ++requestIdRef.current;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const [summaryResponse, sellersResponse] = await Promise.all([
        commissionService.summary(target, { signal: controller.signal }),
        sellerService.list(
          {
            size: SELLERS_PAGE_SIZE,
            sortBy: "name",
            sortDirection: "ASC",
          },
          { signal: controller.signal },
        ),
      ]);
      if (requestIdRef.current !== requestId) return;

      const summaryById = new Map(
        summaryResponse.map((item) => [item.sellerId, item]),
      );
      const merged: CommissionSummaryDTO[] = sellersResponse.content.map(
        (seller) =>
          summaryById.get(seller.id) ?? EMPTY_SUMMARY(seller.id, seller.name),
      );

      // Inclui no fim qualquer sellerId presente no summary mas ausente
      // da listagem de vendedores (defensivo — não deve ocorrer).
      summaryById.forEach((summary, sellerId) => {
        if (!sellersResponse.content.some((s) => s.id === sellerId)) {
          merged.push(summary);
        }
      });

      setData(merged);
    } catch (error: unknown) {
      if (controller.signal.aborted) return;
      if (requestIdRef.current !== requestId) return;
      const message = getApiErrorMessage(error, "Erro ao carregar comissões");
      setError(message);
      toast.error(message);
    } finally {
      if (requestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    // load() dispara setLoading(true) síncrono para spinner imediato;
    // não causa cascading render porque loading não está nas deps.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load(period);
  }, [load, period]);

  // Cleanup no unmount — cancela request em voo.
  useEffect(() => () => controllerRef.current?.abort(), []);

  const reload = useCallback(() => {
    load(period);
  }, [load, period]);

  return {
    data,
    loading,
    error,
    period,
    setPeriod,
    reload,
  };
}
